<template>
	<div
		ref="listRef"
		class="relative flex items-center w-full md:w-[calc(20%-20px)] min-h-[56px] pl-0 sm:pl-4 md:pl-6 pr-0 sm:pr-4 pb-2 md:pb-0 mt-3 md:mt-0"
	>
		<div @click="showList = !showList" class="flex flex-col flex-1 w-[calc(100%-30px)] cursor-pointer">
			<span class="pb-1 text-dj-gray-dark text-sm font-medium">{{ $t("jobs.searchbar.extras.title") }}</span>
			<span
				v-if="selectedExtras.length"
				class="block w-[calc(100%-6px)] whitespace-nowrap overflow-ellipsis overflow-hidden"
			>
				{{ selectedExtras.map((extra) => extra.name[locale]).join(", ") }}
			</span>
			<span v-else class="text-dj-gray whitespace-nowrap overflow-ellipsis overflow-hidden">
				{{ placeholder }}
			</span>
		</div>

		<DropdownButton v-model="showList" class="mt-2" />

		<div
			v-if="showList"
			class="absolute p-5 left-0 top-[60px] md:top-[85px] w-[280px] max-w-[100%] md:max-w-[100vw] h-[max] bg-white border border-dj-gray-light rounded-big flex flex-col gap-y-[10px] z-30"
		>
			<BaseCheckbox
				v-for="(extra, index) in extras"
				:key="index"
				v-model="extra.value"
				@change="onChangeExtra(extra)"
			>
				{{ extra.name[locale] }}
			</BaseCheckbox>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, onMounted, watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { useI18n } from "vue-i18n";

export default defineComponent({
	name: "SearchbarJobsExtras",
	props: {
		modelValue: {
			type: Array as PropType<number[]>,
			required: true,
		},
		placeholder: {
			type: String,
			default: "",
		},
		extrasData: {
			type: Array as PropType<{ id: number; name: Record<string, string> }[]>,
			default: () => [],
		},
	},
	emits: ["update:modelValue", "loaded"],
	setup(props, { emit }) {
		const { locale } = useI18n();
		const route = useRoute();

		type ExtraItem = { id: number; name: Record<string, string> };
		type ExtraItemWithValue = ExtraItem & { value: boolean };
		const selectedExtras = ref<ExtraItemWithValue[]>([]);
		const extras = ref<ExtraItemWithValue[]>([]);

		onMounted(() => {
			emit("loaded");
		});

		watch(
			() => props.extrasData,
			(value) => {
				if (!Array.isArray(value)) {
					extras.value = [];
					selectedExtras.value = [];
					return;
				}
				addValuePropToExtras();
				getValuesFromUrl();
			},
			{ immediate: true }
		);

		const showList = ref(false);
		const listRef = ref(null);
		watch(
			() => props.modelValue,
			(value) => {
				if (value.length == 0) {
					selectedExtras.value = [];
					extras.value.forEach((extra) => {
						extra.value = false;
					});
				}
			}
		);

		function addValuePropToExtras() {
			extras.value = props.extrasData.map((extra) => {
				return {
					...extra,
					value: false,
				};
			});
		}

		function onChangeExtra(extra: ExtraItemWithValue) {
			const index = extras.value.findIndex((pred) => pred.name[locale.value] == extra.name[locale.value]);
			if (index < 0) return;

			if (extras.value[index].value) {
				selectedExtras.value.push(extra);
				emitExtras();
			} else {
				selectedExtras.value = selectedExtras.value.filter(
					(sExtra) => sExtra.name[locale.value] != extra.name[locale.value]
				);
				emitExtras();
			}
		}

		function getValuesFromUrl() {
			const query = route.query;
			selectedExtras.value = [];

			if (!query.extra || extras.value.length === 0) {
				emitExtras();
				return;
			}

			const extrasFromUrl = query.extra
				.toString()
				.split(",")
				.map((t) => Number.parseInt(t, 10))
				.filter((id) => Number.isFinite(id));

			extras.value.forEach((extra, index) => {
				extras.value[index].value = false;
				extrasFromUrl.forEach((extraFU) => {
					if (extra.id == extraFU) {
						extras.value[index].value = true;
						selectedExtras.value.push(extras.value[index]);
					}
				});
			});

			emitExtras();
		}

		function emitExtras() {
			emit(
				"update:modelValue",
				selectedExtras.value.map((extra) => extra.id)
			);
		}

		onClickOutside(listRef, () => {
			showList.value = false;
		});

		return {
			extras,
			selectedExtras,
			showList,
			listRef,
			locale,
			onChangeExtra,
		};
	},
});
</script>
